import { NeutralButton, PrimaryButton } from '@systemeio/ui'
import ContactsListingLayout from 'modules/contacts/components/contact-listing-layout'
import ContactsMenu from 'modules/contacts/components/contacts-menu'
import { ContactsLoco } from 'modules/contacts/constants/contacts-loco'
import { getContactsBinPageNextjsUrl } from 'modules/contacts/contacts-bin/utils/get-contacts-bin-page-nextjs-url'
import { getContactsImportPageNextjsUrl } from 'modules/contacts/contacts-import/utils/get-contacts-import-page-nextjs-url'
import { useApplyFilterWithConditions } from 'modules/contacts/hooks/use-apply-filter-with-conditions'
import { useContactFilterActions } from 'modules/contacts/hooks/use-contact-filter-actions'
import { useContactFilterPagination } from 'modules/contacts/hooks/use-contact-filter-pagination'
import { useContactSavedFilters } from 'modules/contacts/hooks/use-contact-saved-filters'
import { useContacts } from 'modules/contacts/hooks/use-contacts'
import { useFilterWithConditions } from 'modules/contacts/hooks/use-filter-with-conditions'
import { useRenderContactFilters } from 'modules/contacts/hooks/use-render-contact-filters'
import { NextPage } from 'next'
import React, { useState } from 'react'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import { Table } from 'shared/components/table/table'
import { useListingSelect } from 'shared/hooks/use-listing-select'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import useWindowSize from 'shared/hooks/use-window-size'
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props'
import { getContactColumns } from '../../modules/contacts/configs/contact-tabel-colums'
import { AddIcon, ContactIcon, TrashClockIcon } from '@systemeio/ui'
import { OrderEnum } from 'shared/types/pagination-interface'

const Contacts: NextPage = () => {
  const { user, isRestrictedUser } = useUser()
  const { t } = useLocoTranslation()

  const { savedFields, mutateSavedFields } = useContactSavedFilters()

  const { setConditions, conditions, filter, setFilter, filtersReady, clearFilter, filterFields } =
    useFilterWithConditions()

  const [needToCloseFilters, setNeedToCloseFilters] = useState(false)

  const onResetFilters = () => {
    clearFilter()
    setNeedToCloseFilters(true)
  }

  const renderFilters = useRenderContactFilters({
    filter,
    filterFields,
    filtersReady,
    setFilter,
    needToCloseFilters,
    setNeedToCloseFilters,
  })

  const { onSaveNewFilter, onDeleteSavedFilter, applySavedFilters } = useContactFilterActions({
    clearFilter,
    filterFields,
    mutateSavedFields,
    filter,
    setFilter,
  })

  const { setPagination, pagination, setLimit, limit } = useContactFilterPagination({
    conditions,
    setConditions,
  })

  const { contacts, counter, abortContacts, mutate } = useContacts({
    limit,
    pagination,
    conditions,
  })

  const { renderSelectButton, getListingSelect, renderSelectAllButton, isMenuShown } =
    useListingSelect({
      listing: contacts,
      count: counter?.count,
      filter: conditions,
    })

  useApplyFilterWithConditions({
    abort: abortContacts,
    filterFields,
    filtersReady,
    setLimit,
    setConditions,
    conditions,
    filter,
    onFilterChange: () => setPagination({ startFromId: undefined, order: OrderEnum.next }),
  })

  const { width } = useWindowSize()

  return (
    <ContactsListingLayout
      counter={counter}
      data={contacts}
      title={t('dashboard.contacts.title_plural')}
      isMenuShown={isMenuShown}
      filterData={{
        limiter: { limit, setLimit },
        paginator: { pagination, setPagination },
        applySavedFilters,
        onDeleteSavedFilter,
        onSaveNewFilter,
        onResetFilters,
        filterer: {
          setFilter,
          filter,
          filterFields,
          savedFilters: savedFields?.items,
        },
        filterable: renderFilters,
      }}
      customHeaderElement={
        <div className={'flex gap-3'}>
          <PrimaryButton
            as={LinkWithoutPrefetch}
            href={'/contacts/create'}
            aria-disabled={!!isRestrictedUser}
          >
            <AddIcon />
            <span className="hidden lg:flex">{t('dashboard.contacts.create_contact')}</span>
          </PrimaryButton>
          <NeutralButton
            as={LinkWithoutPrefetch}
            className={'[&_svg]:hover:fill-brand-primary [&_svg]:main-transition-colors'}
            href={getContactsImportPageNextjsUrl()}
          >
            <ContactIcon />
            <span className="hidden lg:flex">{t('dashboard.contacts.import_contacts')}</span>
          </NeutralButton>
        </div>
      }
    >
      <div className={'w-full'}>
        {contacts && contacts.items.length !== 0 && (
          <div
            className={
              'bg-brand-primary-subtle flex items-center gap-3 rounded-t-lg px-6 py-4 lg:hidden'
            }
          >
            {renderSelectAllButton()}
            <span>{t('global.select_all')}</span>
          </div>
        )}
        <Table
          firstLineRect={width < 1024 ? !!contacts : false}
          data={contacts}
          columns={getContactColumns({
            renderSelectButton,
            renderSelectAllButton,
            contacts,
            user,
            t,
            width,
          })}
          limiter={{ limit, setLimit }}
          paginator={{ pagination, setPagination }}
          footerAdditionalElement={
            <NeutralButton
              as={LinkWithoutPrefetch}
              className={'[&_svg]:hover:fill-brand-primary [&_svg]:main-transition-colors'}
              href={getContactsBinPageNextjsUrl()}
            >
              <TrashClockIcon />
              <span className="hidden lg:flex">{t('dashboard.contacts.recently_deleted')}</span>
            </NeutralButton>
          }
        />
        <ContactsMenu
          mutateContacts={mutate}
          conditions={conditions}
          fields={filterFields?.items}
          getListingSelect={getListingSelect}
        />
      </div>
    </ContactsListingLayout>
  )
}

export const getServerSideProps = getBaseServerSideProps(ContactsLoco, 'header.top_menu.contacts')

export default Contacts
